import { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Edit, RefreshCw, CheckCircle } from 'lucide-react';
import { docsSearch, docsUpload, docsUpdate, docsDelete, docsSyncDrive } from '../../api';

const CC=['bg-blue-100 text-blue-700','bg-green-100 text-green-700','bg-purple-100 text-purple-700','bg-orange-100 text-orange-700','bg-red-100 text-red-700','bg-yellow-100 text-yellow-700'];

export default function DocumentsTab() {
  const [docs,setDocs]       = useState([]);
  const [loading,setLoad]    = useState(true);
  const [syncing,setSyncing] = useState(false);
  const [syncMsg,setSyncMsg] = useState('');
  const [showUpload,setShowUp]= useState(false);
  const [editDoc,setEditDoc] = useState(null);
  const [form,setForm]       = useState({title:'',category:'',year:'',pages:'',language:'Khmer/English',description:''});
  const fileRef              = useRef();

  useEffect(()=>{ loadDocs(); },[]);

  const loadDocs = ()=>{
    setLoad(true);
    docsSearch('','').then(r=>setDocs(r.data.documents||[])).catch(()=>{}).finally(()=>setLoad(false));
  };

  const syncDrive = async()=>{
    setSyncing(true); setSyncMsg('');
    try{
      const r=await docsSyncDrive();
      setSyncMsg(`✅ ${r.data.message}`);
      loadDocs();
    }catch(e){ setSyncMsg('❌ Sync failed: '+e.message); }
    finally{ setSyncing(false); }
  };

  const handleUpload = async e=>{
    e.preventDefault();
    const file=fileRef.current?.files[0];
    if(!file){ alert('Please select a PDF file'); return; }
    const fd=new FormData();
    Object.entries(form).forEach(([k,v])=>fd.append(k,v));
    fd.append('file',file);
    try{
      await docsUpload(fd);
      setShowUp(false); setForm({title:'',category:'',year:'',pages:'',language:'Khmer/English',description:''});
      loadDocs();
    }catch(e){ alert('Upload failed: '+e.message); }
  };

  const handleEdit = async e=>{
    e.preventDefault();
    try{ await docsUpdate(editDoc.id,form); setEditDoc(null); loadDocs(); }
    catch(e){ alert('Update failed: '+e.message); }
  };

  const handleDelete = async id=>{
    if(!window.confirm('Delete this document from Drive and database?')) return;
    try{ await docsDelete(id); setDocs(d=>d.filter(x=>x.id!==id)); }catch(e){ alert('Delete failed: '+e.message); }
  };

  const openEdit = doc=>{ setEditDoc(doc); setForm({title:doc.title,category:doc.category,year:doc.year||'',pages:doc.pages||'',language:doc.language||'Khmer/English',description:doc.description||''}); };

  return(
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <span className="text-sm text-gray-500">{docs.length} documents in database</span>
        <div className="flex gap-2">
          <button onClick={syncDrive} disabled={syncing} className="flex items-center gap-2 text-sm border border-blue-300 text-blue-700 px-4 py-2 rounded-xl hover:bg-blue-50 disabled:opacity-60 transition">
            <RefreshCw size={14} className={syncing?'animate-spin':''}/>{syncing?'Syncing...':'Sync from Google Drive'}
          </button>
          <button onClick={()=>setShowUp(true)} className="flex items-center gap-2 text-sm bg-blue-900 text-white px-4 py-2 rounded-xl hover:bg-blue-800 transition">
            <Upload size={14}/>Upload New Document
          </button>
        </div>
      </div>
      {syncMsg&&<div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm px-4 py-2 rounded-xl">{syncMsg}</div>}

      {(showUpload||editDoc)&&(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="font-bold text-gray-800 mb-4">{editDoc?'Edit Document':'Upload New Document'}</h3>
            <form onSubmit={editDoc?handleEdit:handleUpload} className="space-y-3">
              {!editDoc&&<div><label className="text-xs font-semibold text-gray-500 block mb-1">PDF File *</label><input type="file" ref={fileRef} accept=".pdf" required className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"/></div>}
              {['title','category','year','pages','language','description'].map(k=>(
                <div key={k}><label className="text-xs font-semibold text-gray-500 block mb-1 capitalize">{k}</label>
                  <input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} required={['title','category'].includes(k)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"/>
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={()=>{setShowUp(false);setEditDoc(null);}} className="flex-1 border border-gray-200 text-gray-500 py-2 rounded-xl text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-900 text-white py-2 rounded-xl text-sm font-semibold hover:bg-blue-800">{editDoc?'Update':'Upload'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading?<div className="text-center py-20 text-gray-400">Loading documents...</div>:(
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-400 bg-gray-50 border-b"><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Year</th><th className="px-4 py-3">Language</th><th className="px-4 py-3">Actions</th></tr></thead>
              <tbody>{docs.map((doc,i)=>(
                <tr key={doc.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium max-w-xs truncate">{doc.title}</td>
                  <td className="px-4 py-3"><span className={"text-xs px-2 py-0.5 rounded-full "+CC[i%CC.length]}>{doc.category}</span></td>
                  <td className="px-4 py-3 text-gray-400">{doc.year||'—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{doc.language}</td>
                  <td className="px-4 py-3"><div className="flex gap-2">
                    <button onClick={()=>openEdit(doc)} className="text-blue-400 hover:text-blue-600"><Edit size={14}/></button>
                    <button onClick={()=>handleDelete(doc.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14}/></button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
